import './date.css';

import _ from 'lodash';
import PropTypes from 'prop-types';
import React, {useEffect, useRef, useState} from 'react';
import InputMask from "react-input-mask";

import Date, {DateHandle} from './Date';
import TextBox from './TextBox';

import {getTime} from '../dateUtils';

const WrapperStyle:React.CSSProperties = {
  display: 'inline-block',
  width: '100%'
};
const DateStyleBordered:React.CSSProperties = {
  float: 'left',
  width: 'calc(100% - 50px)',
  borderRight: '1px solid black'
};
const DateStyle:React.CSSProperties = {
  float: 'left',
  width: 'calc(100% - 50px)',
};
const TimeStyle:React.CSSProperties = {
  float: 'left',
  width: '50px',
};

function validateHhMm(input:string|null) {
  const isValid = /^([0-1]?[0-9]|2[0-4]):([0-5][0-9])(:[0-5][0-9])?$/.test(input ?? '');
  return isValid;
}

export function Time({
  timeId,
  isAllowed=true, // Date control can block time entry until date is set
  value,
  onChange,
  onTabPrevious = undefined,
  onTabNext = undefined,
  disabled,
  noborder,
  name=undefined,
  showPlaceHolder = true,
  noborderLeft = true,
  }: {
    timeId?: string|undefined,
    isAllowed?: boolean,
    value:string|null,
    onChange: (x:string|null) => void,
    disabled: boolean,
    onTabPrevious?: (() => boolean)|undefined,
    onTabNext?: (() => boolean)|undefined,
    noborder?: boolean
    name?: string|undefined,
    showPlaceHolder?: boolean,
    noborderLeft?: boolean
  }) {
  const [timeValue, setTimeValue] = useState('');
  const placeholder = '__:__';

  useEffect(() => {
    mapValueToState();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  function processTabNext(e:React.KeyboardEvent<HTMLInputElement>) {
    if (onTabNext) {
      const tabProcessed = onTabNext();
      if (tabProcessed) {
        e.preventDefault();
        return;
      }
    }
  }

  function processTabPrevious(e:React.KeyboardEvent<HTMLInputElement>) {
    if (onTabPrevious) {
      const tabProcessed = onTabPrevious();
      if (tabProcessed) {
        e.preventDefault();
        return;
      }
    }
  }

  function onKeyDown(e:React.KeyboardEvent<HTMLInputElement>) {
    const tab = 9;

    if (e.keyCode === tab) {
      if (e.shiftKey) {
        processTabPrevious(e);
      } else {
        processTabNext(e);
      }
    }
  }

  function mapValueToState() {
    if (!isAllowed && !!timeValue) {
      setTimeValue('');
      return;
    }

    if (validateHhMm(value)) {
      if (timeValue !== value) {
        setTimeValue(value || '')
      }
    } else {
      setTimeValue('');
    }
  }

  function onChangeRaw(e:React.ChangeEvent<HTMLInputElement>) {
    if (!isAllowed) {
      return;
    }

    const value = e.target.value;
    if (!value || value === placeholder) {
      onChange(null);
      setTimeValue(timeValue);
    } else {
      if (validateHhMm(e.target.value)) {
        onChange(e.target.value)
      }

      setTimeValue(e.target.value);
    }
  }

  function handleBlur() {
    mapValueToState();
  }

  if (disabled) {
    return <TextBox noborder={noborder} disabled name={name} placeholder={showPlaceHolder ? "HH:MM" : ''} value={value} onChange={() => {}} />
  }

  const startsWithTwo = !!timeValue && timeValue[0] === '2';

  let cssClass = 'e-ifms-input e-outline ifms-tabable';
  const mask = startsWithTwo ? '23:99' : '29:99';

  if (!disabled) {
    cssClass = `${cssClass} ifms-tabable-active`;
  }

  return (
    <InputMask
      alwaysShowMask={false}
      mask={mask}
      value={timeValue}
      onChange={onChangeRaw}
      // @ts-ignore:next-line
      formatChars={{
        '9': '[0-9]',
        '2': '[0-2]',
        '3': '[0-3]',
      }}
      onBlur={handleBlur}
    >
      {/*{(inputProps:any) => <input maskChar={null} placeholder="HH:MM" id={timeId} type="text" {...inputProps} className="e-ifms-input e-outline ifms-tabable" autoComplete="off" list="autocompleteOff" data-tooltip="-from-value" data-tooltip-onlywhencutoff />}*/}
      {(inputProps: any) => <input placeholder={showPlaceHolder ? "HH:MM" : ''} id={timeId} name={name} type="text" {...inputProps} className={cssClass} style={{ borderLeft: noborderLeft ? 'none' : '1px solid #767676' }} autoComplete="off" list="autocompleteOff" data-tooltip="-from-value" data-tooltip-onlywhencutoff onKeyDown={onKeyDown} />}
    </InputMask>
  );

}

function DateTime({
  value,
  onChange = () => {},
  disabled = false,
  dateOnly = false,
  dateId = undefined,
  timeId = undefined,
  onTimeTabNext = undefined,
  hideIcon = true,
  blackMode = false,
  noborder = false,
  name=undefined,
  showPlaceHolder = true,
}: {
  value: string|null|undefined,
  onChange: (x:string|null) => void,
  disabled?: boolean,
  dateOnly?: boolean,
  dateId?: string|undefined,
  timeId?: string|undefined,
  onTimeTabNext?: (() => boolean)|undefined,
  hideIcon?: boolean,
  blackMode?: boolean,
  noborder?: boolean,
  name?: string|undefined,
  showPlaceHolder?: boolean,
}) {
  const time = getTime(value);
  const lTimeId = timeId ||  _.uniqueId('ifms-datetime-time-');
  // type DateHandle = React.ElementRef<typeof Date>;
  const dateRef = useRef<DateHandle>(null);

  function onDateChange(d:string|null) {
    if (!d) {
      if (!!value) {
        onChange(null);
      }
    } else {
      const timeToApply = time ?? '00:00';
      const tIndex = d.indexOf('T');
      if (tIndex < 0) {
        throw new Error('Invalid iso date format: ' + d);
      }
      const newDateTime = `${d.substring(0, tIndex)}T${timeToApply}`;
      onChange(newDateTime);

      const timeEl = document.getElementById(lTimeId) as HTMLInputElement;
      if (timeEl) {
        setTimeout(() => {
          timeEl.focus();
          timeEl.select();
        }, 10);     

        if (dateRef && dateRef.current) {
          dateRef.current.setOpen(false);
        }
      }
    }
  }

  function onTimeChange(t:string|null) {
    if (!value) {
      onChange(null);
      return;
    }

    const timeToApply = t ?? '00:00';
    const tIndex = value.indexOf('T');
    if (tIndex < 0) {
      throw new Error('Invalid iso date format: ' + value);
    }

    const newDateTime = `${value.substring(0, tIndex)}T${timeToApply}`;
    onChange(newDateTime);
  }

  return (
    <div style={WrapperStyle}>
      <div style={blackMode ? DateStyleBordered : DateStyle}>
        <Date noborder={noborder} ref={dateRef} dateId={dateId} disabled={disabled} value={value} onChange={onDateChange} hideIcon={hideIcon} iconOffset="-68px" name={`${name}-date`} showPlaceHolder={showPlaceHolder}/>
      </div>

      <div style={TimeStyle}>
        <Time timeId={lTimeId} noborder={noborder} isAllowed={!!value} disabled={dateOnly || disabled} value={time || ''} onChange={onTimeChange} onTabNext={onTimeTabNext} name={`${name}-time`} showPlaceHolder={showPlaceHolder} />
      </div>

      <div className="clearfix" />
    </div>
  );
}

DateTime.defaultProps = {
  onChange: () => {},
  disabled: false,
  dateOnly: false,
  hideIcon: true,
  blackMode: false,
  noborder: false,
};

DateTime.propTypes = {
  dateId: PropTypes.string,
  timeId: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  value: PropTypes.string,
  disabled: PropTypes.bool.isRequired,
  dateOnly: PropTypes.bool.isRequired,
  onTimeTabNext: PropTypes.func,
  hideIcon: PropTypes.bool.isRequired,
  blackMode: PropTypes.bool.isRequired,
  noborder: PropTypes.bool.isRequired,
};


export default DateTime;
